import type { UnifiedAuthStep } from '~kui/types/auth';

import { AuthFlowLayout } from '~kui/types/auth';

import { EDUCATORS_ROUTE_TERMS } from '~/constants/routes/educators';
import { UNIFIED_ROUTE_TERMS } from '~/constants/routes/unified';
import { SiteRoleType } from '~/graphql/generated/graphql';

/** Whether a login door may create an account for a new email, or only lets existing roles in. */
export const LoginDoorSignUpPolicy = {
  Student: 'student',
  None: 'none',
} as const;

// eslint-disable-next-line ts/no-redeclare
export type LoginDoorSignUpPolicy = (typeof LoginDoorSignUpPolicy)[keyof typeof LoginDoorSignUpPolicy];

export const loginDoorSignUpPolicy = (roleType: SiteRoleType): LoginDoorSignUpPolicy =>
  roleType === SiteRoleType.Student ? LoginDoorSignUpPolicy.Student : LoginDoorSignUpPolicy.None;

/**
 * The password-setup step's copy per door. The schools site resets a forgotten password on this
 * step, and staff are pointed at My Account, not a student profile; students keep the kit copy.
 */
export const loginDoorPasswordSetupCopy = (roleType: SiteRoleType): { descriptionKey: string | null; tokenExpiredKey: string } =>
  roleType === SiteRoleType.Student
    ? { descriptionKey: null, tokenExpiredKey: 'unified.auth.passwordSetup.tokenExpired' }
    : { descriptionKey: 'schools.auth.passwordSetup.description', tokenExpiredKey: 'schools.auth.passwordSetup.tokenExpired' };

const PADLOCK_IMAGE = '/images/coach/padlock.webp';

/** Students keep the kit's own step images; the staff doors swap in their own artwork. */
const doorStepImages = (roleType: SiteRoleType): Partial<Record<UnifiedAuthStep, string>> => {
  if (roleType === SiteRoleType.Student) {
    return {};
  }

  const isTeacher = roleType === SiteRoleType.Teacher;
  const rocket = isTeacher ? '/images/auth/rocket-blue.webp' : '/images/auth/rocket.webp';
  const envelope = isTeacher ? '/images/auth/envelope-pink.webp' : '/images/auth/envelope.webp';

  return {
    'email': rocket,
    'method': rocket,
    'password': PADLOCK_IMAGE,
    'otp': envelope,
    'choose-account': rocket,
    'password-setup': PADLOCK_IMAGE,
    'register-disabled': PADLOCK_IMAGE,
    'mfa-enrol': PADLOCK_IMAGE,
    'mfa-challenge': PADLOCK_IMAGE,
  };
};

const DISCLAIMER_STEPS: readonly UnifiedAuthStep[] = ['email', 'otp', 'method', 'password'];

/** What each login door (student, educators, content admin) flavours on the shared login flow. */
export const useLoginDoorConfig = (roleType: SiteRoleType) => {
  const { t } = useI18n();
  const route = useRoute();
  const router = useRouter();
  const { registerDisabledHelpTextKey } = usePlatformAppConfig(route.path);

  const isStudent = roleType === SiteRoleType.Student;
  const passwordSetupCopy = loginDoorPasswordSetupCopy(roleType);

  // The links are rendered as raw anchors (v-html and `target="_blank"`), which bypass the router
  // base, so they are resolved here to carry the cluster path prefix.
  const termsHref = router.resolve(roleType === SiteRoleType.Teacher ? EDUCATORS_ROUTE_TERMS : UNIFIED_ROUTE_TERMS).href;
  const privacyHref = router.resolve('/privacy-policy').href;

  const disclaimer = isStudent ? '' : t('general.auth.enterEmailDisclaimer', { termsUrl: termsHref, privacyUrl: privacyHref });

  const disclaimerFor = (step: UnifiedAuthStep) => (DISCLAIMER_STEPS.includes(step) ? disclaimer : '');

  return {
    termsHref,
    disclaimerFor,
    helpText: isStudent ? '' : t('general.auth.enterEmailHeader'),
    passwordSetupDescription: passwordSetupCopy.descriptionKey ? t(passwordSetupCopy.descriptionKey) : '',
    registerDisabledHelpText: registerDisabledHelpTextKey ? t(registerDisabledHelpTextKey) : '',
    stepImages: doorStepImages(roleType),
    showSocial: isStudent,
    signUpPolicy: loginDoorSignUpPolicy(roleType),
    layout: isStudent ? AuthFlowLayout.Public : AuthFlowLayout.Embedded,
  };
};
